/**
 * Parse scores from BÖLÜM 8 (Puanlama) content
 * Puanlama is always the 8th section regardless of language
 */

export interface ScoreData {
  label: string;
  score: number; // 0-100 scale for radar chart
  description?: string; // Detailed explanation/justification
}

/**
 * Extract general score (GENEL PUAN) from puanlama content
 */
export function parseGenelPuan(content: string): number | null {
  if (!content) {
    console.log('parseGenelPuan: content is empty or null');
    return null;
  }
  
  // Normalize content: handle HTML tags by converting to text
  let normalizedContent = content;
  if (typeof document !== 'undefined' && content.includes('<')) {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      normalizedContent = tempDiv.innerText || tempDiv.textContent || content;
      console.log('parseGenelPuan: Converted HTML to text, length:', normalizedContent.length);
    } catch (e) {
      console.log('parseGenelPuan: Error parsing HTML, using original content');
      normalizedContent = content;
    }
  }
  
  // Try both original and normalized content
  const contentSources = [content, normalizedContent];
  
  // Try various patterns for GENEL PUAN
  // Support both old format and new format (with brackets)
  const patterns = [
    // Pattern 1: "GENEL PUAN: [6.9]/10" (with brackets, new format)
    /GENEL\s+PUAN\s*:?\s*\[?(\d+[.,]\d+|\d+)\]?\s*\/\s*10/i,
    // Pattern 2: "GENEL PUAN: 6.9/10" (without brackets)
    /GENEL\s+PUAN\s*:?\s*(\d+[.,]\d+|\d+)\s*\/\s*10/i,
    // Pattern 3: "GENEL PUAN: 6.9" (without /10)
    /GENEL\s+PUAN\s*:?\s*(\d+[.,]\d+|\d+)/i,
    // Pattern 4: "Genel Puan: [6.9]/10" (mixed case with brackets)
    /Genel\s+Puan\s*:?\s*\[?(\d+[.,]\d+|\d+)\]?\s*\/\s*10/i,
    // Pattern 5: "Genel Puan: 6.9/10" (mixed case without brackets)
    /Genel\s+Puan\s*:?\s*(\d+[.,]\d+|\d+)\s*\/\s*10/i,
    // Pattern 6: "Genel Puan: 6.9" (mixed case without /10)
    /Genel\s+Puan\s*:?\s*(\d+[.,]\d+|\d+)/i,
    // Pattern 7: "**GENEL PUAN**: **9.3/10**" (bold markdown format)
    /\*\*GENEL\s+PUAN\*\*:\s*\*\*(\d+[.,]\d+|\d+)\s*\/\s*10\*\*/i,
    // Pattern 8: "**Genel Puan**: **9.3/10**" (bold markdown format mixed case)
    /\*\*Genel\s+Puan\*\*:\s*\*\*(\d+[.,]\d+|\d+)\s*\/\s*10\*\*/i,
    // Pattern 9: More flexible - any text before colon, then score
    /GENEL\s+PUAN[:\s]+(\d+[.,]\d+|\d+)(?:\s*\/\s*10)?/i,
  ];
  
  for (const source of contentSources) {
    for (let i = 0; i < patterns.length; i++) {
      const pattern = patterns[i];
      const match = source.match(pattern);
      if (match && match[1]) {
        const scoreStr = match[1].replace(',', '.');
        const score = parseFloat(scoreStr);
        if (!isNaN(score) && score >= 0 && score <= 10) {
          console.log(`parseGenelPuan: Bulundu (pattern ${i+1}, source: ${source === content ? 'original' : 'normalized'}): ${score}/10`);
          return score;
        }
      }
    }
  }
  
  console.log('parseGenelPuan: GENEL PUAN bulunamadı.');
  console.log('Original content preview (first 500 chars):', content.substring(0, 500));
  console.log('Normalized content preview (first 500 chars):', normalizedContent.substring(0, 500));
  
  // Last resort: search for any number followed by /10 near "GENEL" or "PUAN"
  const lastResortPattern = /(?:GENEL|PUAN|Genel|Puan)[\s\S]{0,100}?(\d+[.,]\d+|\d+)\s*\/\s*10/i;
  const lastResortMatch = normalizedContent.match(lastResortPattern);
  if (lastResortMatch && lastResortMatch[1]) {
    const scoreStr = lastResortMatch[1].replace(',', '.');
    const score = parseFloat(scoreStr);
    if (!isNaN(score) && score >= 0 && score <= 10) {
      console.log(`parseGenelPuan: Bulundu (last resort pattern): ${score}/10`);
      return score;
    }
  }
  
  return null;
}

/**
 * Parse scoring categories from puanlama content
 * Simplified approach: look for patterns like:
 * - "1. Konsept ve Özgünlük: [8]/10"
 * - "**Konsept ve Özgünlük**: **8/10**"
 * - "Konsept ve Özgünlük: 8/10"
 */
export function parseScoresFromPuanlama(content: string): ScoreData[] {
  if (!content) {
    console.log('parseScoresFromPuanlama: content is empty or null');
    return [];
  }

  // Debug: log first 1000 chars to see actual format
  console.log('parseScoresFromPuanlama: Content preview (first 1000 chars):', content.substring(0, 1000));
  
  // Also try to find any score patterns in the content for debugging
  const debugPattern = /(\d+\.\s*)?([^:\n]{5,}?):\s*(\d+(?:\.\d+)?)\s*\/\s*10/gi;
  const debugMatches: string[] = [];
  let debugMatch;
  debugPattern.lastIndex = 0;
  while ((debugMatch = debugPattern.exec(content.substring(0, 2000))) !== null && debugMatches.length < 10) {
    if (!debugMatch[2].toLowerCase().includes('genel puan')) {
      debugMatches.push(`${debugMatch[2].trim()}: ${debugMatch[3]}/10`);
    }
  }
  console.log('parseScoresFromPuanlama: Found potential score patterns:', debugMatches);

  // Normalize content: handle HTML tags by converting to text
  // First, try to extract text from HTML if present
  let normalizedContent = content;
  
  // If content has HTML tags, extract text content but preserve structure
  if (typeof document !== 'undefined' && content.includes('<')) {
    try {
      // Create a temporary div to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      // Get text content but try to preserve some structure
      normalizedContent = tempDiv.innerText || tempDiv.textContent || content;
      console.log('parseScoresFromPuanlama: Converted HTML to text, length:', normalizedContent.length);
    } catch (e) {
      console.log('parseScoresFromPuanlama: Error parsing HTML, using original content');
      normalizedContent = content;
    }
  }

  // Expected categories in order
  const expectedCategories = [
    'Konsept ve Özgünlük',
    'Hikaye Yapısı ve Akıcılık',
    'Karakter Derinliği ve İnandırıcılığı',
    'Karakter Gelişimi ve İzleyici Bağı',
    'Anlatım Dili ve Atmosfer',
    'Diyalogların Kalitesi',
    'Tematik Derinlik',
    'Ticari Potansiyel ve Pazarlanabilirlik',
  ];

  // Patterns to match score formats - try most specific first
  // Try both original content (for markdown) and normalized content (for HTML)
  const contentSources = [content, normalizedContent];
  
  const patterns = [
    // Pattern 1: "1.  **Konsept ve Özgünlük:** 7/10" (with number, : inside bold, score NOT bold) - MOST COMMON
    /\d+\.\s+\*\*([^*]+?):\*\*\s+(\d+(?:\.\d+)?)\s*\/\s*10/gi,
    // Pattern 2: "**Konsept ve Özgünlük:** 7/10" (without number, : inside bold, score NOT bold)
    /\*\*([^*]+?):\*\*\s+(\d+(?:\.\d+)?)\s*\/\s*10/gi,
    // Pattern 3: "1.  **Konsept ve Özgünlük:** **8/10**" (with number, : inside bold, score bold)
    /\d+\.\s+\*\*([^*]+?):\*\*\s*\*\*(\d+(?:\.\d+)?)\s*\/\s*10\*\*/gi,
    // Pattern 4: "**Konsept ve Özgünlük:** **8/10**" (without number, : inside bold, score bold)
    /\*\*([^*]+?):\*\*\s*\*\*(\d+(?:\.\d+)?)\s*\/\s*10\*\*/gi,
    // Pattern 5: "1.  **Konsept ve Özgünlük**: **8/10**" (with number, : outside bold)
    /\d+\.\s+\*\*([^*]+?)\*\*:\s*\*\*(\d+(?:\.\d+)?)\s*\/\s*10\*\*/gi,
    // Pattern 6: "**Konsept ve Özgünlük**: **8/10**" (without number, : outside bold)
    /\*\*([^*]+?)\*\*:\s*\*\*(\d+(?:\.\d+)?)\s*\/\s*10\*\*/gi,
    // Pattern 7: HTML format with <strong> tags: "1. <strong>Konsept ve Özgünlük:</strong> 7/10"
    /\d+\.\s*<strong>([^<]+?):<\/strong>\s+(\d+(?:\.\d+)?)\s*\/\s*10/gi,
    // Pattern 8: HTML format with <strong> tags: "1. <strong>Konsept ve Özgünlük:</strong> <strong>8/10</strong>"
    /\d+\.\s*<strong>([^<]+?):<\/strong>\s*<strong>(\d+(?:\.\d+)?)\s*\/\s*10<\/strong>/gi,
    // Pattern 9: "1. Konsept ve Özgünlük: [8]/10" or "Konsept ve Özgünlük: [8]/10"
    /(?:\d+\.\s*)?([^:\n]+?):\s*\[?(\d+(?:\.\d+)?)\]?\s*\/\s*10/gi,
    // Pattern 10: "Konsept ve Özgünlük: 8/10" (simple format, case-insensitive start)
    /([A-ZÇĞİÖŞÜa-zçğıöşü][^:\n]{2,}?):\s*(\d+(?:\.\d+)?)\s*\/\s*10/gi,
  ];

  const matches: Array<{ name: string; score: number; index: number; description?: string }> = [];
  const seenCategories = new Set<string>();
  
  // Try each pattern on both original and normalized content
  // But only process each source once to avoid duplicates
  const processedIndices = new Set<number>();
  
  for (const source of contentSources) {
    for (const pattern of patterns) {
      pattern.lastIndex = 0; // Reset regex
      let match;
      
      while ((match = pattern.exec(source)) !== null) {
        // Skip if we've already processed this exact match position
        const matchKey = `${source === content ? 'orig' : 'norm'}_${match.index}_${match[0]}`;
        if (processedIndices.has(match.index)) {
          continue;
        }
        processedIndices.add(match.index);
      // Extract category name and score from match groups
      // All patterns should have category in group 1 and score in group 2
      let categoryName: string;
      let scoreStr: string;
      
      if (match.length >= 3 && match[1] && match[2]) {
        // Standard patterns: category in group 1, score in group 2
        categoryName = match[1].trim();
        scoreStr = match[2].replace(',', '.');
      } else {
        console.log('parseScoresFromPuanlama: Invalid match structure, skipping:', match);
        continue;
      }
      
      // Skip if it's GENEL PUAN (check before processing)
      const categoryNameLower = categoryName.toLowerCase();
      if (categoryNameLower.includes('genel puan') || categoryNameLower.trim() === 'genel puan') {
        continue;
      }
      
      console.log('parseScoresFromPuanlama: Pattern match found:', {
        fullMatch: match[0],
        category: categoryName,
        score: scoreStr,
        index: match.index
      });
      
      try {
        const score = parseFloat(scoreStr);
        if (isNaN(score) || score < 0 || score > 10) {
          continue;
        }
        
        // Clean category name (remove markdown and HTML tags)
        categoryName = categoryName
          .replace(/\*\*|__|\*|_/g, '')
          .replace(/<[^>]*>/g, '')
          .trim();
        
        if (categoryName.length < 3) {
          continue;
        }
        
        // Skip if it's GENEL PUAN (check again after cleaning)
        if (categoryName.toLowerCase().includes('genel puan')) {
          continue;
        }
          
        // Try to match with expected categories
        let matchedCategory: string | null = null;
        const categoryLower = categoryName.toLowerCase();
        
        for (const expected of expectedCategories) {
          const expectedLower = expected.toLowerCase();
          // Flexible matching: exact, contains, or significant words match
          if (categoryLower === expectedLower || 
              categoryLower.includes(expectedLower) || 
              expectedLower.includes(categoryLower)) {
            matchedCategory = expected;
            break;
          }
          
          // Check if significant words match
          const categoryWords = categoryLower.split(/\s+/).filter(w => w.length > 2);
          const expectedWords = expectedLower.split(/\s+/).filter(w => w.length > 2);
          const matchingWords = categoryWords.filter(w => 
            expectedWords.some(ew => ew.includes(w) || w.includes(ew))
          );
          if (matchingWords.length >= 2) {
            matchedCategory = expected;
            break;
          }
        }
        
        const finalCategory = matchedCategory || categoryName;
        const categoryKey = finalCategory.toLowerCase();
        
        // Only add if not seen before
        if (!seenCategories.has(categoryKey)) {
          // Try to extract description
          let description: string | undefined;
          const matchEnd = match.index! + match[0].length;
          // Find next score match or end of content
          let nextMatchIndex = source.length;
          for (const p of patterns) {
            p.lastIndex = matchEnd;
            const nextMatch = p.exec(source);
            if (nextMatch && nextMatch.index < nextMatchIndex) {
              nextMatchIndex = nextMatch.index;
            }
          }
          const sectionEnd = Math.min(matchEnd + 2000, nextMatchIndex);
          const sectionText = source.substring(matchEnd, sectionEnd);
          
          // Look for description patterns
          const descPatterns = [
            /Özgün Gerekçe[^:]*:\s*([\s\S]*?)(?=\n\s*(?:\d+\.|GENEL|BÖLÜM|$))/i,
            /Gerekçe[^:]*:\s*([\s\S]*?)(?=\n\s*(?:\d+\.|GENEL|BÖLÜM|$))/i,
            /\*\*Gerekçe\*\*:\s*([\s\S]*?)(?=\n\s*(?:\d+\.|GENEL|BÖLÜM|$))/i,
          ];
          
          for (const descPattern of descPatterns) {
            const descMatch = sectionText.match(descPattern);
            if (descMatch && descMatch[1]) {
              description = descMatch[1]
                .replace(/\*\*|__|\*|_/g, '')
                .replace(/\n{3,}/g, '\n\n')
                .trim();
              if (description.length > 500) {
                description = description.substring(0, 500) + '...';
              }
              break;
            }
          }
          
          matches.push({
            name: finalCategory,
            score: score,
            index: match.index || 0,
            description: description
          });
          seenCategories.add(categoryKey);
          console.log(`parseScoresFromPuanlama: Added: ${finalCategory} = ${score}/10`);
        }
      } catch (e) {
        console.log(`parseScoresFromPuanlama: Error parsing score:`, e);
        continue;
      }
      }
    }
  }

  // Sort by expected category order
  const sortKey = (item: typeof matches[0]) => {
    const index = expectedCategories.indexOf(item.name);
    return index >= 0 ? index : expectedCategories.length + 1;
  };
  
  matches.sort((a, b) => sortKey(a) - sortKey(b));

  // Convert to ScoreData format (score 0-100 for radar chart)
  const results: ScoreData[] = matches.slice(0, 8).map(match => ({
    label: match.name,
    score: match.score * 10, // Convert 0-10 to 0-100
    description: match.description
  }));

  console.log(`parseScoresFromPuanlama: Found ${results.length} scoring categories`);
  return results;
}
